import { useEffect, useState } from "react";
import axios from "axios";
import { Download, Gift, CalendarDays, X, Mail, User as UserIcon, Loader2, Check, Users } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COVER_IMG = "/CAPA_VERAO_THUMB.jpg";
const BOOKLET_PDF = "/TREINOS_VERAO_O-MEU-PT.pdf";

const PLANOS = [
  {
    codigo: "A",
    titulo: "Full Body Clássico",
    meta: "Sem equipamento",
    file: "/TREINO_FERIAS_A_SEM_EQUIPAMENTO.pdf",
  },
  {
    codigo: "B",
    titulo: "Full Body Dinâmico",
    meta: "Sem equipamento",
    file: "/TREINO_FERIAS_B_SEM_EQUIPAMENTO.pdf",
  },
  {
    codigo: "C",
    titulo: "Full Body c/ Halteres",
    meta: "1 par de halteres",
    file: "/TREINO_FERIAS_C_HALTERES.pdf",
  },
];

// Show only during August (any year). Change to `true` to force-show.
export function isPlanosVeraoActive() {
  const now = new Date();
  return now.getMonth() === 7; // 0 = Jan ... 7 = August
}

function DownloadModal({ open, onClose, onSuccess, selectedFile, selectedLabel }) {
  const [nome, setNome] = useState("");
  const [email, setEmail] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) {
      setNome("");
      setEmail("");
      setError("");
      setSaving(false);
    }
  }, [open]);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!nome.trim()) return setError("Diz-me como te chamas.");
    if (!/^\S+@\S+\.\S+$/.test(email.trim())) return setError("Verifica o teu email.");
    setSaving(true);
    try {
      const res = await axios.post(`${API}/planos-verao/request`, {
        nome: nome.trim(),
        email: email.trim().toLowerCase(),
      });
      onSuccess(res.data?.downloads || null);
      // Trigger the actual browser download of the selected file
      const link = document.createElement("a");
      link.href = selectedFile || BOOKLET_PDF;
      link.download = "";
      link.target = "_blank";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      setError(err?.response?.data?.detail || "Não foi possível processar. Tenta de novo.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
      data-testid="planos-verao-modal"
      onClick={onClose}
    >
      <div
        className="bg-neutral-950 border border-neutral-800 max-w-md w-full p-6 md:p-8 relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-neutral-400 hover:text-yellow-400 transition-colors"
          data-testid="planos-verao-modal-close"
          aria-label="Fechar"
        >
          <X size={22} />
        </button>

        <div className="flex items-center gap-2 text-yellow-400 text-[10px] uppercase tracking-[0.32em] font-black mb-3">
          <span className="w-6 h-[2px] bg-yellow-400" />
          <Gift size={12} />
          <span>Oferta de Verão</span>
        </div>

        <h3 className="font-display text-3xl md:text-4xl uppercase tracking-tighter leading-tight mb-2">
          Recebe o teu <br />
          <span className="text-yellow-400">
            {selectedLabel || "booklet completo"}
          </span>
        </h3>
        <p className="text-neutral-400 text-sm mb-6 leading-relaxed">
          Preenche abaixo e enviamos-te o PDF para o email. A descarga começa
          também imediatamente.
        </p>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-yellow-400 font-bold mb-2 flex items-center gap-2">
              <UserIcon size={12} /> O teu nome
            </div>
            <input
              type="text"
              value={nome}
              onChange={(e) => setNome(e.target.value)}
              className="w-full bg-neutral-950 border border-neutral-800 focus:border-yellow-400 outline-none text-white px-4 py-3 text-sm transition-colors"
              placeholder="Ex.: Ana Silva"
              data-testid="planos-verao-form-nome"
              autoFocus
            />
          </div>

          <div>
            <div className="text-[10px] uppercase tracking-widest text-yellow-400 font-bold mb-2 flex items-center gap-2">
              <Mail size={12} /> Email
            </div>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full bg-neutral-950 border border-neutral-800 focus:border-yellow-400 outline-none text-white px-4 py-3 text-sm transition-colors"
              placeholder="ana@exemplo.com"
              data-testid="planos-verao-form-email"
            />
          </div>

          {error ? (
            <div className="text-red-400 text-xs" data-testid="planos-verao-form-error">
              {error}
            </div>
          ) : null}

          <button
            type="submit"
            disabled={saving}
            className="w-full bg-yellow-400 hover:bg-yellow-300 disabled:opacity-60 disabled:cursor-not-allowed text-black font-black uppercase tracking-widest text-sm py-4 flex items-center justify-center gap-3 transition-colors"
            data-testid="planos-verao-form-submit"
          >
            {saving ? (
              <>
                <Loader2 size={16} className="animate-spin" /> A preparar…
              </>
            ) : (
              <>
                <Download size={16} /> Descarregar agora
              </>
            )}
          </button>

          <div className="text-[10px] uppercase tracking-widest text-neutral-500 text-center pt-2">
            Prometemos zero spam · só o essencial
          </div>
        </form>
      </div>
    </div>
  );
}

function SuccessToast({ show, downloads, onClose }) {
  useEffect(() => {
    if (!show) return;
    const t = setTimeout(onClose, 6000);
    return () => clearTimeout(t);
  }, [show, onClose]);

  if (!show) return null;

  return (
    <div
      className="fixed bottom-6 right-6 z-50 bg-neutral-950 border border-yellow-400/60 shadow-2xl max-w-sm p-5"
      data-testid="planos-verao-toast"
    >
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 flex items-center justify-center bg-yellow-400 text-black flex-shrink-0">
          <Check size={20} strokeWidth={3} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-white font-black text-sm uppercase tracking-wider mb-1">
            Booklet a caminho!
          </div>
          <div className="text-neutral-400 text-xs leading-relaxed">
            A descarga começou e enviámos também o PDF para o teu email.
          </div>
          {downloads ? (
            <div className="text-yellow-400 text-[10px] uppercase tracking-widest mt-2 font-bold">
              Já somos {downloads} este verão ⚡
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export default function PlanosVeraoSection() {
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(BOOKLET_PDF);
  const [selectedLabel, setSelectedLabel] = useState("Booklet Completo");
  const [downloads, setDownloads] = useState(0);
  const [showToast, setShowToast] = useState(false);

  useEffect(() => {
    if (!isPlanosVeraoActive()) return;
    let mounted = true;
    axios
      .get(`${API}/planos-verao/stats`)
      .then((r) => {
        if (mounted) setDownloads(r.data?.downloads || 0);
      })
      .catch(() => {});
    return () => {
      mounted = false;
    };
  }, []);

  if (!isPlanosVeraoActive()) return null;

  const openWith = (file, label) => {
    setSelectedFile(file);
    setSelectedLabel(label);
    setModalOpen(true);
  };

  const onSuccess = (newCount) => {
    if (newCount != null) setDownloads(newCount);
    setModalOpen(false);
    setShowToast(true);
  };

  return (
    <>
      <section
        id="planos-verao"
        className="relative px-6 md:px-12 lg:px-24 py-16 md:py-24 border-t border-neutral-900 overflow-hidden"
        data-testid="planos-verao-section"
      >
        <div
          className="pointer-events-none absolute -top-32 -right-32 w-[560px] h-[560px] rounded-full blur-3xl opacity-[0.12]"
          style={{ background: "radial-gradient(circle, #ffd60a 0%, transparent 65%)" }}
          aria-hidden="true"
        />

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          {/* Cover thumbnail */}
          <div className="relative order-2 lg:order-1">
            <div
              className="absolute -inset-4 rounded-sm bg-yellow-400/20 blur-2xl"
              aria-hidden="true"
            />
            <button
              type="button"
              onClick={() => openWith(BOOKLET_PDF, "Booklet Completo")}
              className="relative block group w-full text-left"
              data-testid="planos-verao-cover-link"
            >
              <img
                src={COVER_IMG}
                alt="Capa · Verão com O-MEU-PT.PT"
                className="relative w-full h-auto border border-neutral-800 shadow-[0_30px_80px_-20px_rgba(255,214,10,0.25)] transition-transform duration-500 group-hover:scale-[1.015]"
                data-testid="planos-verao-cover-image"
              />
              <div
                className="absolute top-4 left-4 bg-yellow-400 text-black font-black text-[10px] tracking-[0.24em] uppercase px-3 py-1.5"
                data-testid="planos-verao-cover-ribbon"
              >
                Grátis · Agosto
              </div>
            </button>
          </div>

          {/* Content */}
          <div className="order-1 lg:order-2">
            <div className="flex items-center gap-3 text-yellow-400 text-[10px] uppercase tracking-[0.32em] font-black mb-4">
              <span className="w-8 h-[2px] bg-yellow-400" />
              <Gift size={14} />
              <span>Em Destaque · Oferta de Verão</span>
            </div>

            <h2
              className="font-display text-4xl md:text-5xl lg:text-6xl leading-[0.98] tracking-tighter uppercase text-white"
              data-testid="planos-verao-title"
            >
              Planos Treinos <br />
              <span className="text-yellow-400">Férias Grátis</span>
            </h2>

            <p
              className="mt-6 text-neutral-300 text-base md:text-lg max-w-xl leading-relaxed"
              data-testid="planos-verao-subtitle"
            >
              3 planos full body em circuito para não parares nas férias — sem
              equipamento ou com 1 par de halteres. 15 min por volta, faz 3 a 5.
              Onde quer que estejas.
            </p>

            <div className="mt-6 flex flex-wrap gap-2 text-[11px] font-bold uppercase tracking-widest">
              <span className="border border-yellow-400/40 text-yellow-300 px-3 py-1.5">
                8 exercícios
              </span>
              <span className="border border-yellow-400/40 text-yellow-300 px-3 py-1.5">
                ≈ 15 min / volta
              </span>
              <span className="border border-yellow-400/40 text-yellow-300 px-3 py-1.5">
                3 a 5 voltas
              </span>
              <span className="border border-yellow-400/40 text-yellow-300 px-3 py-1.5">
                Todos os níveis
              </span>
            </div>

            <button
              type="button"
              onClick={() => openWith(BOOKLET_PDF, "Booklet Completo")}
              className="group mt-8 inline-flex items-center gap-3 bg-yellow-400 hover:bg-yellow-300 text-black font-black uppercase tracking-widest text-sm px-7 py-4 transition-colors"
              data-testid="planos-verao-download-booklet"
            >
              <Download size={18} />
              <span>Descarregar Booklet Completo</span>
              <span className="text-black/50 text-[10px] tracking-widest">PDF · 5 pág</span>
            </button>

            <div className="mt-6">
              <div className="text-[10px] uppercase tracking-[0.32em] text-neutral-500 font-black mb-3">
                Ou descarrega individualmente
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
                {PLANOS.map((p) => (
                  <button
                    key={p.codigo}
                    type="button"
                    onClick={() => openWith(p.file, p.titulo)}
                    className="group border border-neutral-800 hover:border-yellow-400/70 bg-neutral-950/60 hover:bg-neutral-950 transition-all p-4 flex items-center gap-3 text-left"
                    data-testid={`planos-verao-download-${p.codigo.toLowerCase()}`}
                  >
                    <div className="text-2xl font-black text-yellow-400 leading-none w-6">
                      {p.codigo}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-white font-bold text-[13px] leading-tight">
                        {p.titulo}
                      </div>
                      <div className="text-neutral-500 text-[10px] uppercase tracking-widest mt-1">
                        {p.meta}
                      </div>
                    </div>
                    <Download
                      size={16}
                      className="text-neutral-500 group-hover:text-yellow-400 transition-colors flex-shrink-0"
                    />
                  </button>
                ))}
              </div>
            </div>

            <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-3 text-neutral-500 text-xs tracking-wide">
              <div className="flex items-center gap-2">
                <CalendarDays size={14} className="text-yellow-400" />
                <span>Disponível durante todo o mês de Agosto</span>
              </div>
              {downloads > 0 ? (
                <div
                  className="flex items-center gap-2"
                  data-testid="planos-verao-downloads-count"
                >
                  <Users size={14} className="text-yellow-400" />
                  <span>
                    <b className="text-yellow-400">{downloads}</b>{" "}
                    {downloads === 1 ? "pessoa já descarregou" : "pessoas já descarregaram"} este verão
                  </span>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </section>

      <DownloadModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSuccess={onSuccess}
        selectedFile={selectedFile}
        selectedLabel={selectedLabel}
      />

      <SuccessToast
        show={showToast}
        downloads={downloads}
        onClose={() => setShowToast(false)}
      />
    </>
  );
}
